import { cn } from "cn"
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "lucide-react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { dismissToast, subscribeToasts, type Toast, type ToastKind } from "@/lib/toast"

const STYLES: Record<ToastKind, { title: string; accent: string; icon: string; Icon: typeof Info }> = {
  success: { title: "Success", accent: "bg-success", icon: "bg-success-soft text-success ring-success-border", Icon: CircleCheck },
  error: { title: "Error", accent: "bg-destructive", icon: "bg-destructive-soft text-destructive ring-destructive-border", Icon: CircleAlert },
  warning: { title: "Warning", accent: "bg-warning", icon: "bg-warning-soft text-warning ring-warning-border", Icon: TriangleAlert },
  info: { title: "Info", accent: "bg-primary", icon: "bg-primary-soft text-primary ring-primary-border", Icon: Info },
}

function ToastCard({ toast }: { toast: Toast }) {
  const style = STYLES[toast.kind]

  useEffect(() => {
    // Errors stay longer so they can be read.
    const timeout = setTimeout(() => dismissToast(toast.id), toast.kind === "error" ? 10_000 : 4_000)
    return () => clearTimeout(timeout)
  }, [toast.id, toast.kind])

  return (
    <div
      role="status"
      className="pointer-events-auto relative w-full max-w-sm overflow-hidden rounded-lg border bg-card text-card-foreground shadow-xl animate-in fade-in slide-in-from-bottom-3"
    >
      <span className={cn("absolute inset-y-0 left-0 w-1", style.accent)} aria-hidden="true" />
      <div className="flex items-start gap-3 py-3.5 pr-4 pl-5">
        <div className={cn("mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full ring-1", style.icon)}>
          <style.Icon className="size-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1 pr-1">
          <p className="text-sm leading-5 font-semibold">{style.title}</p>
          <p className="mt-0.5 text-sm leading-5 break-words whitespace-pre-line text-muted-foreground">{toast.message}</p>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Dismiss notification"
          onClick={() => dismissToast(toast.id)}
          className="-mr-1 text-subtle-foreground hover:text-foreground"
        >
          <X aria-hidden="true" />
        </Button>
      </div>
    </div>
  )
}

/** Shows toasts in the bottom-right corner: server flash messages and client messages. */
export function Toaster() {
  const [toasts, setToasts] = useState<Toast[]>([])

  useEffect(() => subscribeToasts(setToasts), [])

  return (
    // Bottom right, clear of the page's action buttons at the top.
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex flex-col-reverse items-end gap-3 px-4 sm:px-6">
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} />
      ))}
    </div>
  )
}
